import {
  Component,
  computed,
  effect,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import {
  AgendaDetailedService,
  TimeFormatPipe,
} from '../agenda-detailed-service';
import {
  CalendarClickEvent,
  CalendarDropTask,
  CalendarTask,
  CalendarTasksGroup,
  CommonUtils,
  HourSelectionEvent,
  Time,
  WeekSelectionEvent,
} from '@infodata-group/inf-commons';
import { AgendaDetailedTask } from './agenda-detailed-task/agenda-detailed-task';
import { AgendaDetailedSubCell } from '../agenda-detailed-sub-cell';
import { AgendaDetailedRow } from '../agenda-detailed-row';
import { ResizingEvent } from '../resizing-event';
import { DayNotWorkService } from '../../day-not-work.service';

@Component({
  selector: 'infl-agenda-detailed-row',
  imports: [TimeFormatPipe, AgendaDetailedTask],
  standalone: true,
  templateUrl: './agenda-detailed-row.component.html',
  styleUrl: './agenda-detailed-row.component.scss',
})
export class AgendaDetailedRowComponent implements OnDestroy {
  protected readonly agendaDetailedService = inject(AgendaDetailedService);
  protected readonly dayNotWorkService = inject(DayNotWorkService);

  public row = input<AgendaDetailedRow>(null);
  public outsideDraggingTask = input<CalendarDropTask<any>>(null);
  public resourceWidth = input(300);

  public periodHovered = output<WeekSelectionEvent>();
  public periodSelected = output<WeekSelectionEvent>();
  public taskClick = output<CalendarClickEvent>();
  public taskHover = output<CalendarTask>();
  public taskUpdate = output<CalendarTask>();
  public taskCreation = output<CalendarTask>();

  private selectedCells: AgendaDetailedSubCell[] = [];
  public selection: { start: Time; end: Time } = { start: null, end: null };

  // For resizing
  private resizeStart: number;
  public resizeTask: {
    border: 'top' | 'bottom';
    viewTask: { task: CalendarTask; style: any };
  };
  private baseHeight: number;
  private baseTop: number;

  public backgroundDayColor = computed(() => {
    if (this.row() && !this.dayNotWorkService.isDayWork(this.row().date)) {
      return this.dayNotWorkService.dayNotWorkColor;
    }
    return 'var(--white)';
  });

  private interval: any;
  public timeLine = signal<
    {
      time: string;
      top: string;
    }[]
  >([]);

  constructor() {
    effect(() => {
      if (this.outsideDraggingTask()) {
        this.managePointerEvents('none');
      } else {
        this.managePointerEvents('auto');
      }
    });
    effect(() => {
      if (this.row()) {
        this.generateHoursTimeLine();
      }
      if (this.row().isCurrent) {
        this.createHourLineStyle();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  private createHourLineStyle() {
    this.computeHourLineHeight();
    this.interval = setInterval(() => {
      this.computeHourLineHeight();
    }, 60000);
  }

  public hourLineTop = signal(0);

  private computeHourLineHeight() {
    const startInMinutes =
      this.row().options.morningStart.hours * 60 +
      this.row().options.morningStart.minutes;
    const endInMinutes =
      this.row().options.afternoonEnd.hours * 60 +
      this.row().options.afternoonEnd.minutes;
    const diff = endInMinutes - startInMinutes;
    const pixelPerMinute = this.row().height / diff;
    const today = new Date();
    const currentTimeInMinute = today.getHours() * 60 + today.getMinutes();
    const difference = currentTimeInMinute - startInMinutes;
    if (currentTimeInMinute < startInMinutes) {
      this.hourLineTop.set(0);
    } else if (currentTimeInMinute > endInMinutes) {
      this.hourLineTop.set(this.row().height);
    } else {
      this.hourLineTop.set(difference * pixelPerMinute);
    }
  }

  private generateHoursTimeLine() {
    const viewTimes = [];
    let top = 0;
    for (const cell of this.row().resourcesCellsDay[0].subCells) {
      if (top === 0) {
        if (cell.timeStart.minutes === 0) {
          viewTimes.push({
            time: CommonUtils.convertTimeToString(cell.timeStart),
            top: `${top}px`,
          });
        }
        top = cell.height - 7;
        continue;
      }
      if (cell.timeStart.minutes === 0) {
        viewTimes.push({
          time: CommonUtils.convertTimeToString(cell.timeStart),
          top: `${top}px`,
        });
      }
      top += cell.height;
    }
    const lastCell = this.row().resourcesCellsDay[0].subCells.at(-1);
    if (lastCell.timeEnd.minutes === 0) {
      viewTimes.push({
        time: CommonUtils.convertTimeToString(lastCell.timeEnd),
        top: `${top - 7}px`,
      });
    }
    this.timeLine.set(viewTimes);
  }

  protected onSelectCell(cell: AgendaDetailedSubCell) {
    if (!this.row().options.canSelect) {
      return;
    }
    cell.isSelected = true;
    this.selectedCells.push(cell);
    this.selection = { start: cell.timeStart, end: null };
  }

  protected onOverCell(
    event: MouseEvent,
    cell: AgendaDetailedSubCell,
    resource: CalendarTasksGroup,
  ) {
    if (this.selectedCells.length === 0) {
      const selectionEvent = new HourSelectionEvent(
        {
          start: this.cloneTime(cell.timeStart),
          end: this.cloneTime(cell.timeEnd),
        },
        event,
      );
      selectionEvent.resource = new CalendarTasksGroup(
        resource.id,
        resource.label,
      );
      this.periodHovered.emit(
        new WeekSelectionEvent(this.row().date, selectionEvent),
      );
      return;
    }
    if (this.selection.start) {
      this.selection.end = cell.timeStart;
      this.checkSelectedCells();
    }
  }

  private getSelectionBounds() {
    const start =
      this.selection.start.hours * 60 + this.selection.start.minutes;

    const end = this.selection.end.hours * 60 + this.selection.end.minutes;

    return {
      min: Math.min(start, end),
      max: Math.max(start, end),
    };
  }

  private checkSelectedCells() {
    const { min, max } = this.getSelectionBounds();
    const allCells = this.row()
      .resourcesCellsDay.flatMap((res) => res.subCells)
      .filter((c) => c.resource.id === this.selectedCells[0].resource.id);
    for (const cell of allCells) {
      const cellTime = cell.timeStart.hours * 60 + cell.timeStart.minutes;
      cell.isSelected = cellTime >= min && cellTime <= max;
    }
    this.selectedCells = allCells.filter((c) => c.isSelected);
  }

  private cloneTime(time: Time) {
    return { hours: time.hours, minutes: time.minutes };
  }

  private checkTime() {
    if (!this.selectedCells.length) {
      return { start: null, end: null };
    }
    let startMinutes = Infinity;
    let endMinutes = -Infinity;
    for (const cell of this.selectedCells) {
      const cellStart = cell.timeStart.hours * 60 + cell.timeStart.minutes;
      const cellEnd = cell.timeEnd.hours * 60 + cell.timeEnd.minutes;

      startMinutes = Math.min(startMinutes, cellStart);
      endMinutes = Math.max(endMinutes, cellEnd);
    }
    const start = {
      hours: Math.floor(startMinutes / 60),
      minutes: startMinutes % 60,
    };
    const end = {
      hours: Math.floor(endMinutes / 60),
      minutes: endMinutes % 60,
    };
    return { start, end };
  }

  protected onUnselectCell(event: MouseEvent, resource: CalendarTasksGroup) {
    const period = this.checkTime();
    const selectionEvent = new HourSelectionEvent(
      {
        start: this.cloneTime(period.start),
        end: this.cloneTime(period.end),
      },
      event,
    );
    selectionEvent.resource = new CalendarTasksGroup(
      resource.id,
      resource.label,
    );
    this.periodSelected.emit(
      new WeekSelectionEvent(this.row().date, selectionEvent),
    );
  }

  public clearCells() {
    this.selectedCells.forEach((cell) => (cell.isSelected = false));
    this.selectedCells = [];
    this.selection = { start: null, end: null };
  }

  protected onDragStart(task: CalendarTask) {
    this.agendaDetailedService.draggingTask = task;
    this.managePointerEvents('none');
  }

  public managePointerEvents(value: 'none' | 'auto') {
    setTimeout(() => {
      const tasks = document.getElementsByClassName('agenda-detailed-task');
      for (let i = 0; i < tasks.length; i++) {
        (tasks[i] as HTMLElement).style.pointerEvents = value;
      }
    }, 100);
  }

  protected onDragEnterCell(event: DragEvent, cell: AgendaDetailedSubCell) {
    event.preventDefault();
    cell.isHovered = true;
  }

  protected onDragLeaveCell(cell: AgendaDetailedSubCell) {
    cell.isHovered = false;
  }

  protected onDropTask(cell: AgendaDetailedSubCell) {
    this.agendaDetailedService.dropTask(cell);
    if (this.agendaDetailedService.outsideDraggingTask) {
      this.taskCreation.emit(this.agendaDetailedService.draggingTask);
      this.agendaDetailedService.outsideDraggingTask = null;
    } else if (this.agendaDetailedService.draggingTask) {
      this.taskUpdate.emit(this.agendaDetailedService.draggingTask);
    }
    this.managePointerEvents('auto');
    this.agendaDetailedService.draggingTask = null;
  }

  @HostListener('document:mouseup')
  stopResizeTask() {
    if (!this.resizeTask) {
      return;
    }
    this.resizeTask.viewTask.style.opacity = '1';
    const top = Number(this.resizeTask.viewTask.style.top.replace('px', ''));
    const height = Number(
      this.resizeTask.viewTask.style.height.replace('px', ''),
    );
    const pixelPerMinute = this.row().options.hoursSection === 1 ? 2 : 1;
    const timeStartInMinutes = top * pixelPerMinute;
    const duration = height * pixelPerMinute;
    const start =
      this.row().options.morningStart.hours * 60 +
      this.row().options.morningStart.minutes +
      timeStartInMinutes;
    const end = start + duration;
    const minutesToRounded = this.row().options.hoursSection === 1 ? 60 : 30;
    const startRound = Math.round(start / minutesToRounded) * minutesToRounded;
    const endRound = Math.round(end / minutesToRounded) * minutesToRounded;
    const startTime = { hours: startRound / 60, minutes: startRound % 60 };
    const endTime = { hours: endRound / 60, minutes: endRound % 60 };
    const task = this.resizeTask.viewTask.task;
    task.dateStart = task.dateStart
      .hour(Math.floor(startTime.hours))
      .minute(startTime.minutes);
    task.dateEnd = task.dateEnd
      .hour(Math.floor(endTime.hours))
      .minute(endTime.minutes);
    this.taskUpdate.emit(task);
    setTimeout(() => {
      this.resizeTask = null;
    }, 100);
  }

  onResizeTask(event: MouseEvent, row: HTMLDivElement) {
    if (!this.resizeTask) {
      return;
    }
    if (this.resizeTask.border === 'top') {
      const top = this.baseTop + (event.y - this.resizeStart);
      if (top < 0) {
        return;
      }
      this.resizeTask.viewTask.style.top = `${top}px`;
      this.resizeTask.viewTask.style.height = `${
        this.baseHeight - (event.y - this.resizeStart)
      }px`;
      return;
    }
    if (
      this.baseTop + (event.y - this.resizeStart) + this.baseHeight >=
      row.clientHeight
    ) {
      return;
    }
    this.resizeTask.viewTask.style.height = `${
      this.baseHeight + (event.y - this.resizeStart)
    }px`;
  }

  protected startResizeTask(resizeEvent: ResizingEvent) {
    this.resizeStart = resizeEvent.event.y;
    const target = (<any>resizeEvent.event.target).offsetParent;
    this.baseHeight = target.clientHeight;
    this.baseTop = target.offsetTop;
    this.resizeTask = {
      viewTask: {
        task: resizeEvent.viewTask.task,
        style: resizeEvent.viewTask.style,
      },
      border: resizeEvent.direction,
    };
    this.resizeTask.viewTask.style.opacity = '0.3';
  }

  protected onTaskClick(event: CalendarClickEvent) {
    if (this.resizeTask) {
      return;
    }
    this.taskClick.emit(event);
  }

  protected onMouseLeave($event: MouseEvent, resource: CalendarTasksGroup) {
    if (!this.selection.start) {
      return;
    }
    this.onUnselectCell($event, resource);
  }
}
